import useToggle from '../../../../hooks/useToggle';
import { Box, Dialog, DialogContent, FormControlLabel } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import React from 'react';
import { MOBILE } from '@/app/constants/dictionaries/ScreenSizeConst';
import { hideGlobalDialog } from '@/app/state/ui/dialog';
import DialogTitle from '../../../dialogs/DialogTitle';
import TranslationHelper from '../../../../helpers/TranslationHelper';
import { Field, Formik } from 'formik';
import { useValidationSchema } from './hooks/useValidationSchema';

import { TDeviceDialogType } from './types';
import { IDevice } from '@/app/state/types';
import LoadingSpinner from '@/app/components/loadingSpinner/LoadingSpinner';
import CheckboxWrapper from '@/app/components/common/formikWrappers/CheckboxWrapper';
import { useDeviceInitialValues } from './hooks/useDeviceInitialValues';
import { useAppDispatch } from '@/app/hooks';
import { useDeviceSave } from './hooks/useDeviceSave';
import { DeviceDialogTabs } from './DeviceDialogTabs/DeviceDialogTabs';

const TITLES: Record<string, string> = {
    create: 'New device',
    edit: 'Edit device',
    editMultiple: 'Edit multiple objects',
};

interface DeviceDialogProps {
    mode: TDeviceDialogType;
    selectedDevice?: IDevice;
}

export const DeviceDialog = ({ mode, selectedDevice }: DeviceDialogProps) => {
    const { isOpen, handleToggle } = useToggle(true);
    const isMobile = useMediaQuery(MOBILE);
    const dispatch = useAppDispatch();

    const handleClose = (reason?: string) => {
        if (reason === 'backdropClick') return;
        dispatch(hideGlobalDialog());
        handleToggle();
    };

    const validationSchema = useValidationSchema(mode);
    const { handleSave } = useDeviceSave(mode, handleClose);
    const initialValues = useDeviceInitialValues(selectedDevice);

    return (
        <Dialog
            open={isOpen}
            onClose={(_: object, reason?: string) => handleClose(reason)}
            fullScreen={isMobile}
            maxWidth={'lg'}
        >
            {!initialValues ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: 800,
                        minHeight: 800,
                    }}
                >
                    <LoadingSpinner />
                </Box>
            ) : (
                <Formik
                    initialValues={initialValues}
                    onSubmit={handleSave}
                    validateOnBlur={false}
                    validateOnChange={false}
                    validationSchema={validationSchema}
                >
                    {({ submitForm, errors, values, setFieldValue }) => (
                        <>
                            <DialogTitle
                                title={TITLES[mode] ?? ''}
                                close={handleClose}
                                save={submitForm}
                            />
                            <DialogContent
                                sx={{
                                    minWidth: 800,
                                    height: 800,
                                    width: !isMobile ? 800 : undefined,
                                }}
                            >
                                {mode === 'editMultiple' ? (
                                    <FormControlLabel
                                        label={TranslationHelper.translate(
                                            'Send data to eToll'
                                        )}
                                        control={
                                            <Field
                                                name={'attributes.eToll'}
                                                component={CheckboxWrapper}
                                                onClick={() =>
                                                    setFieldValue(
                                                        'advanced.eToll.enabled',
                                                        !values.advanced?.eToll
                                                            ?.enabled
                                                    )
                                                }
                                            />
                                        }
                                    />
                                ) : (
                                    <DeviceDialogTabs
                                        mode={mode}
                                        errors={errors}
                                        values={values}
                                        setFieldValue={setFieldValue}
                                    />
                                )}
                            </DialogContent>
                        </>
                    )}
                </Formik>
            )}
        </Dialog>
    );
};
